import * as React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import styles from './style.module.scss';
import PaginationItem from '@mui/material/PaginationItem';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { testIds } from 'constants/test-constants';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_NAV_PAGE_CHANGE } from 'constants/events';
import { useThemeContext } from 'contexts';
import { ThemeNames } from 'types/themes';
import { getVisiblePageItems, PAGE_ELLIPSIS_THRESHOLD } from './get-visible-page-items';

export { PAGE_ELLIPSIS_THRESHOLD, getVisiblePageItems } from './get-visible-page-items';

/** Pagination display modes */
export enum PaginationMode {
  NUMBERED = 'numbered',
  PREVNEXT = 'prevnext'
}

export type NumberedPaginationProps = {
  mode?: PaginationMode.NUMBERED; // Optional, defaults to NUMBERED
  pageNumber: number;
  totalPages: number;
  setPageNumber: (pageNumber: number) => void;
};

export type PrevNextPaginationProps = {
  mode: PaginationMode.PREVNEXT;
  pageNumber: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
};


export type PaginationNavProps = NumberedPaginationProps | PrevNextPaginationProps;

const theme = createTheme({
  components: {
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          border: 'none',
          '&.MuiPaginationItem-colorPrimary.Mui-selected': {
            backgroundColor: '#dceef8',
            color: '#31587f',
            '&:hover': {
              backgroundColor: '#dceef8',
            },
          },
        },
      },
    },
  },
});

function NumberedPagination({ pageNumber, totalPages, setPageNumber }: NumberedPaginationProps) {
  const { themeName } = useThemeContext();
  const isExpTheme = themeName && themeName !== ThemeNames.ZOOCASA;
  const selectedColor = isExpTheme ? 'primary' : 'standard';

  if (totalPages <= 1) {
    return null;
  }

  const visibleItems = getVisiblePageItems(pageNumber, totalPages);

  const goToPage = (value: number) => {
    if (value === pageNumber) return;
    trackEvent(GTM_CLICK_NAV_PAGE_CHANGE);
    setPageNumber(value);
  };

  return (
    <ThemeProvider theme={theme}>
      <div
        className={styles.component}
        data-testid={testIds.pageNavigation}
        data-page={pageNumber}
        data-total-pages={totalPages}
        data-uses-ellipsis={totalPages > PAGE_ELLIPSIS_THRESHOLD}
      >
        <nav aria-label="pagination">
          <ul className={styles.list}>
            <li>
              <PaginationItem
                type="previous"
                color={selectedColor}
                disabled={pageNumber <= 1}
                aria-label="Go to previous page"
                onClick={() => goToPage(pageNumber - 1)}
                slots={{ previous: ChevronLeftIcon }}
              />
            </li>
            {visibleItems.map((item, index) => (
              <li key={item === 'ellipsis' ? `ellipsis-${index}` : `page-${item}`}>
                {item === 'ellipsis' ? (
                  <span className={styles.ellipsis} aria-hidden="true">…</span>
                ) : (
                  <PaginationItem
                    type="page"
                    page={item}
                    selected={item === pageNumber}
                    color={selectedColor}
                    aria-label={item === pageNumber ? `page ${item}` : `Go to page ${item}`}
                    aria-current={item === pageNumber ? 'page' : undefined}
                    onClick={() => goToPage(item)}
                  />
                )}
              </li>
            ))}
            <li>
              <PaginationItem
                type="next"
                color={selectedColor}
                disabled={pageNumber >= totalPages}
                aria-label="Go to next page"
                onClick={() => goToPage(pageNumber + 1)}
                slots={{ next: ChevronRightIcon }}
              />
            </li>
          </ul>
        </nav>
      </div>
    </ThemeProvider>
  );
}

function PrevNextPagination({ pageNumber, totalPages, onPrev, onNext }: PrevNextPaginationProps) {
  const hasPrev = pageNumber > 1;
  const hasNext = pageNumber < totalPages;

  const handlePrev = () => {
    trackEvent(GTM_CLICK_NAV_PAGE_CHANGE);
    onPrev();
  };

  const handleNext = () => {
    trackEvent(GTM_CLICK_NAV_PAGE_CHANGE);
    onNext();
  };
  // Hide buttons if both previous and next are not allowed
  if (!hasPrev && !hasNext) {
    return null;
  }

  return (
    <div
      className={styles.component}
      data-testid={testIds.pageNavigation}
      data-page={pageNumber}
      data-total-pages={totalPages}
    >
      <div className={styles.prevNextRow}>
        <button
          className={styles.prevButton}
          onClick={handlePrev}
          disabled={!hasPrev}
          aria-label="Previous page"
          data-testid="pagination-prev"
        >
          <ChevronLeftIcon />
          <span>Prev</span>
        </button>
        <button
          className={styles.nextButton}
          onClick={handleNext}
          disabled={!hasNext}
          aria-label="Next page"
          data-testid="pagination-next"
        >
          <span>Next</span>
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
}



/**
 * Pagination Nav Component. There's two modes: Numbered (traditional list of page number buttons) and Previous/Next buttons (ie. for cursor based nav)
 * @param props
 * @returns
 */
export default function PaginationNav(props: PaginationNavProps) {
  // Default to numbered mode if not provided
  const mode = props.mode ?? PaginationMode.NUMBERED;

  if (mode === PaginationMode.NUMBERED) {
    return <NumberedPagination {...(props as NumberedPaginationProps)} mode={PaginationMode.NUMBERED} />;
  }

  if (mode === PaginationMode.PREVNEXT) {
    return <PrevNextPagination {...(props as PrevNextPaginationProps)} />;
  }
}
